import { addRendition } from '@shared/utils/photoRendition';
import type { RenditionSize } from '@shared/utils/photoRendition';
import { BACKDROP_BASE_URL, BACKDROP_IDS, BACKDROP_TONES } from './digitalCardBackdrop.constants';
import type { BackdropTone } from './digitalCardBackdrop.constants';

/**
 * The backdrop image a stored backdrop names, or undefined for a colour. The wallpaper colour and the
 * backdrop image share `digitalCustomizations.backgroundColor` -- an image is stored by its id, e.g.
 * `botanical-banana` -- so every surface that paints it asks this first. Only a listed id resolves, like
 * `getDigitalCardMusicUrl`, so a removed image falls back to nothing rather than a broken request.
 */
export const getDigitalCardBackdropUrl = (value: string | undefined) =>
  value && (BACKDROP_IDS as ReadonlyArray<string>).includes(value) ? `${BACKDROP_BASE_URL}/${value}.jpg` : undefined;

export const isBackdropImage = (value: string | undefined): value is string => !!getDigitalCardBackdropUrl(value);

/** The gallery's images; `id` is what the draft stores. */
export const DIGITAL_CARD_BACKDROPS = BACKDROP_IDS.map(id => ({ id, url: `${BACKDROP_BASE_URL}/${id}.jpg` }));

/**
 * Whether text drawn straight onto a stored backdrop image should be dark (`light` image) or light
 * (`dark` image), or undefined for a colour, which callers measure themselves. Precomputed per image by
 * `yarn card:digital-backdrop-tones` -- see `BACKDROP_TONES`.
 */
export const getBackdropTone = (value: string | undefined): BackdropTone | undefined =>
  isBackdropImage(value) ? BACKDROP_TONES[value as (typeof BACKDROP_IDS)[number]] : undefined;

/** The URL to paint a stored backdrop image at, or undefined for a colour. */
export const getBackdropImageUrl = (value: string | undefined, renditionSize: RenditionSize = 'xlarge') => {
  const url = getDigitalCardBackdropUrl(value);
  return url && addRendition({ url, renditionSize });
};

/**
 * Box props that paint a stored backdrop on a fixed-size box. A colour is painted as it is; an image
 * covers the box, over a neutral fill that shows while it loads. A box that grows with its content wants
 * `useDigitalCardBackdrop` instead, or the image scales up with it.
 */
export const getBackdropStyleProps = (value: string | undefined, renditionSize: RenditionSize = 'xlarge') => {
  const imageUrl = getBackdropImageUrl(value, renditionSize);
  if (!imageUrl) {
    return { backgroundColor: value };
  }
  return {
    backgroundColor: 'mono2',
    backgroundImage: `url(${imageUrl})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  };
};
